import { GoodAndServiceLeftPanelSide } from 'app/src/features/Shopping/Direct/GoodAndService/components/GoodAndServiceLeftPanelSide'
import { GoodAndServiceRightPanelSide } from 'app/src/features/Shopping/Direct/GoodAndService/components/GoodAndServiceRightPanelSide'
import { GoodAndServiceWidget } from 'app/src/features/Shopping/Direct/GoodAndService/components/GoodAndServiceWidget'
import { shoppingApi } from 'app/src/services/welfareportal'
import { CheckCatalogButton, ErrorCancelPage, PortalConfirmDialog, PortalDialog } from 'app/src/shared/components'
import { CancelSuccess } from 'app/src/shared/components/CancelSuccess'
import { useHttpClient, useWebAppContext } from 'app/src/shared/hooks'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function GoodAndServiceDetails(props: { order: shoppingApi.OrderDetails }) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.GoodAndService' })
  const context = useWebAppContext()
  const httpClient = useHttpClient()
  const [showUsageModal, setShowUsageModal] = useState(false)
  const [activateResult, setActivateResult] = useState<shoppingApi.ActivateResult | null>()
  const [formRef] = useState(useRef<HTMLFormElement>(null))
  const [productActivationModalVisible, setProductActivationModalVisible] = useState<boolean>(false)
  const orderDetailsPurchaseGoodAndService: shoppingApi.OrderDetailsPurchaseGoodAndService = props.order
    ?.purchaseDetails as shoppingApi.OrderDetailsPurchaseGoodAndService
  const [showModal, setShowModal] = useState(false)
  const deleteOrder = shoppingApi.useOrderCancel()
  const [errorComponentVisible, setErrorComponentVisible] = useState(false)
  const [successComponentVisible, setSuccessComponentVisible] = useState(false)

  const handleOrderCancellation = async () => {
    try {
      await deleteOrder.mutateAsync({
        pathParams: { orderId: props.order.orderId },
      })
      setShowModal(false)
      setSuccessComponentVisible(true)
    } catch (error) {
      setErrorComponentVisible(true)
      console.error('Failed to cancel order:', error)
    }
  }

  useEffect(() => {
    if (activateResult) {
      try {
        if (activateResult.method === 'POST') {
          if (formRef?.current) {
            formRef.current.submit()
            setProductActivationModalVisible(false)
          } else {
            context.logger?.error('Error occurred with Form')
            throw new Error('Activating Form Error')
          }
        } else {
          if (activateResult.method === 'GET') {
            if (activateResult.target) {
              window.open(activateResult.url, activateResult.target)
              setProductActivationModalVisible(false)
            } else {
              window.open(activateResult.url, '_self')
              setProductActivationModalVisible(false)
            }
          }
        }
      } catch (err) {
        context.logger?.error('Error activating product', { error: err })
        context.showToastMessage({ content: 'Error activating product', severity: 'error' })
        setProductActivationModalVisible(false)
      } finally {
        setActivateResult(undefined)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activateResult])

  const renderDetailExternalPurchase = useMemo(() => {
    if (orderDetailsPurchaseGoodAndService?.purchaseDescription || orderDetailsPurchaseGoodAndService?.purchaseOrderId)
      return (
        <div className='printable my-10 grid grid-cols-12'>
          <div className='col-span-12 rounded-[10px] border-[3px] border-solid border-[var(--primary-color)] p-3'>
            {orderDetailsPurchaseGoodAndService?.purchaseDescription && (
              <div>
                <div className='text-[16px] font-bold leading-6'>{t('detailExternalPurchaseTitleDesc')}</div>
                <div
                  className='list-container-bullet-disc mt-1 text-wrap py-2 pl-10 text-[16px] font-normal leading-6'
                  dangerouslySetInnerHTML={{ __html: orderDetailsPurchaseGoodAndService.purchaseDescription }}
                ></div>
              </div>
            )}

            {orderDetailsPurchaseGoodAndService?.purchaseOrderId && (
              <p className='my-1 text-[16px] font-bold leading-6'>
                {t('detailExternalPurchaseTitleId')}
                <span className='text-[16px] font-normal leading-6'> {orderDetailsPurchaseGoodAndService.purchaseOrderId}</span>
              </p>
            )}
          </div>
        </div>
      )
    else return
  }, [t, orderDetailsPurchaseGoodAndService?.purchaseDescription, orderDetailsPurchaseGoodAndService?.purchaseOrderId])

  const renderMerchantLogin = useMemo(() => {
    if (orderDetailsPurchaseGoodAndService?.merchantLogin)
      return (
        <div className='noPrint mb-10 mt-5 grid grid-cols-12 text-center'>
          <p className='col-span-12 text-lg font-normal leading-6'>
            {t('merchantLoginP1')}
            <span className='text-lg font-bold leading-6'>{t('merchantLoginP2')}</span>
          </p>
          <p className='col-span-12 text-lg font-normal leading-6'>{t('merchantLoginP3')}</p>
          <div className='col-span-12 mt-5'>
            <div>
              <Button role='link' rounded onClick={() => setProductActivationModalVisible(true)}>
                {t('merchantLoginBtnLabel')}
              </Button>
            </div>
            <p className='col-span-12 my-2 text-sm font-normal leading-5'>{t('merchantLoginP4')}</p>
          </div>
        </div>
      )
    else return
  }, [t, orderDetailsPurchaseGoodAndService?.merchantLogin])

  const onConfirmProductActivationModal = useCallback(async () => {
    if (orderDetailsPurchaseGoodAndService?.merchantLogin?.loginUrl) {
      try {
        const resp = await httpClient.executeJson<shoppingApi.ActivateResult>({
          input: orderDetailsPurchaseGoodAndService?.merchantLogin?.loginUrl,
          init: { method: 'POST' },
        })
        if (resp.jsonResponse) setActivateResult(resp.jsonResponse)
        else {
          context.logger?.error(t('errorMerchantLogin'), { error: (resp?.httpResponse as Response)?.status })
          context.showToastMessage({ content: t('errorMerchantLogin'), severity: 'error' })
          setProductActivationModalVisible(false)
        }
      } catch (err) {
        context.logger?.error(t('errorMerchantLogin'), { error: err })
        context.showToastMessage({ content: t('errorMerchantLogin'), severity: 'error' })
        setProductActivationModalVisible(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [httpClient, context, orderDetailsPurchaseGoodAndService?.merchantLogin?.loginUrl])

  const renderPersonalDataDiscalimer = useMemo(() => {
    if (
      orderDetailsPurchaseGoodAndService?.merchantLogin?.personalData &&
      orderDetailsPurchaseGoodAndService?.merchantLogin?.personalData.length > 0
    )
      return (
        <div className='col-span-12 mt-8'>
          <p className='mb-1'>
            <b>{t('personalDataDiscalimer.title')}</b> <br />
            <b>{t('personalDataDiscalimer.description')}</b>
          </p>
          <ul className='pl-10' style={{ listStyleType: 'disc' }}>
            {orderDetailsPurchaseGoodAndService?.merchantLogin?.personalData?.map((x) => <li key={x}>{x}</li>)}
          </ul>
          {orderDetailsPurchaseGoodAndService?.merchantLogin?.personalDataDisclaimer && (
            <p className='mt-5'> {orderDetailsPurchaseGoodAndService?.merchantLogin?.personalDataDisclaimer}</p>
          )}
        </div>
      )
  }, [t, orderDetailsPurchaseGoodAndService?.merchantLogin])
  if (errorComponentVisible) {
    return (
      <ErrorCancelPage
        errorTitle={t('cancelOrder.cancelErrorMessage.title')}
        errorSubtitle={t('cancelOrder.cancelErrorMessage.subtitle')}
      >
        <div className='mx-auto flex justify-center'>
          <CheckCatalogButton />
        </div>
      </ErrorCancelPage>
    )
  }
  if (successComponentVisible) {
    return (
      <CancelSuccess
        successTitle={t('cancelOrder.cancelSuccessMessage.title')}
        successSubTitle={t('cancelOrder.cancelSuccessMessage.subtitle')}
      >
        <div className='mx-auto flex justify-center'>
          <CheckCatalogButton />
        </div>
      </CancelSuccess>
    )
  }
  if (deleteOrder.isPending)
    return (
      <ProgressSpinner
        data-testid='loading-element'
        pt={{
          root: {
            className: 'w-[50px] h-[50px]',
          },
        }}
      />
    )
  return (
    <div className='mx-auto max-w-5xl'>
      <div className='noPrint'>
        {activateResult && (
          <form
            ref={formRef}
            action={activateResult?.url}
            target={activateResult?.target}
            method={activateResult?.method?.toString()}
          >
            {activateResult?.payload &&
              Object.keys(activateResult.payload).map((x) => (
                <input key={x} name={x} value={activateResult?.payload?.[x]} type='text' hidden />
              ))}
          </form>
        )}
        <div className='col-span-12'>
          {productActivationModalVisible && (
            <PortalDialog
              ptHeader='py-3'
              onHide={() => {
                setProductActivationModalVisible(false)
              }}
              footerProps={{
                okCallBack: () => onConfirmProductActivationModal(),
                okLabel: t('productActivationModal.okBtnLabel'),
                cancelCallback: () => {
                  setProductActivationModalVisible(false)
                },
                cancelLabel: t('productActivationModal.cancelBtnLabel'),
              }}
            >
              <h2 className='text-xl font-bold'>{t('productActivationModal.title')}</h2>
              {renderPersonalDataDiscalimer}
            </PortalDialog>
          )}
        </div>
        <h1 className='welfareportal-titlecenter'>
          {props.order?.priceDetails?.grossPrice === 0 ? t('detailPageTitleGratis') : t('detailPageTitle')}
        </h1>
        {props.order?.isCancelable && props.order.status !== 'canceled' && (
          <div className='flex justify-end'>
            <Button link onClick={() => setShowModal(true)} icon='pi pi-trash' label={t('cancelOrder.cancelButtonLabel')} />
          </div>
        )}
        <div className='card grid grid-cols-1 divide-x-0 divide-y-2 p-5 lg:grid-cols-12 lg:divide-x-2 lg:divide-y-0'>
          <GoodAndServiceLeftPanelSide
            statusMode
            order={props.order}
            setShowUsageModal={setShowUsageModal}
            showUsageModal={showUsageModal}
          />
          <GoodAndServiceRightPanelSide order={props.order} />
        </div>
      </div>
      {renderDetailExternalPurchase}
      {renderMerchantLogin}
      <GoodAndServiceWidget order={props.order} />
      {props.order.status !== 'canceled' ? (
        <div className='mt-4 flex justify-center'>
          <Button
            role='link'
            rounded
            outlined
            onClick={() => window.print()}
            link={true}
            text={true}
            pt={{
              root: { className: 'underline' },
            }}
          >
            {t('getYourCodeButtonLabel')}
          </Button>
        </div>
      ) : null}
      {t('informativeBannerDetailsMode') && <div dangerouslySetInnerHTML={{ __html: t('informativeBannerDetailsMode') }} />}
      {showModal && (
        <PortalConfirmDialog
          body={t('cancelOrder.body')}
          title={t('cancelOrder.title')}
          reject={() => setShowModal(false)}
          accept={handleOrderCancellation}
        />
      )}
    </div>
  )
}
